/**
 * PartnerApplicationsPage — Admin overzicht van inkomende partner-aanmeldingen.
 *
 * Toont alle aanmeldingen met filter per status + kind. Admin kan approve
 * (genereert tijdelijk wachtwoord, activeert account, verstuurt welkomstmail
 * met Calendly-link) of reject (status='rejected').
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  CheckCircle, XCircle, Loader2, RefreshCw, Eye, Mail, Phone, Globe,
  Building, MapPin, Users, ClipboardCopy, X, Sparkles,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const KIND_BADGE_STYLES = {
  affiliate: { bg: '#7c3aed', label: 'Affiliate' },
  retailer: { bg: '#0ea5e9', label: 'Retailer' },
  callcenter: { bg: '#ef4444', label: 'Callcenter' },
  salespartner: { bg: '#f59e0b', label: 'Salespartner' },
  door_to_door: { bg: '#10b981', label: 'Door-to-door' },
  consultant: { bg: '#6366f1', label: 'Consultant' },
  marketingpartner: { bg: '#ec4899', label: 'Marketingpartner' },
};

const STATUS_BADGE_STYLES = {
  pending: { bg: '#f59e0b', label: 'In behandeling' },
  approved: { bg: '#10b981', label: 'Goedgekeurd' },
  rejected: { bg: '#ef4444', label: 'Afgewezen' },
};

export default function PartnerApplicationsPage() {
  const { getToken } = useAuth();
  const token = getToken();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [kindFilter, setKindFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [approving, setApproving] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [approveResult, setApproveResult] = useState(null);
  const [initialPassword, setInitialPassword] = useState('');
  const [approveNotes, setApproveNotes] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (kindFilter) params.kind = kindFilter;
      const res = await axios.get(`${BACKEND}/api/admin/partner-applications`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setRows(res.data?.partners || []);
      setStats(res.data?.stats || { pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      toast.error('Kon partner-aanmeldingen niet laden');
    }
    setLoading(false);
  }, [token, statusFilter, kindFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (r.email || '').toLowerCase().includes(s) ||
      (r.full_name || '').toLowerCase().includes(s) ||
      (r.company_name || '').toLowerCase().includes(s)
    );
  });

  const openApprove = (app) => {
    setDetail(app);
    setShowApprove(true);
    setApproveResult(null);
    setInitialPassword(generatePassword());
    setApproveNotes('');
  };

  const doApprove = async () => {
    if (!detail || !initialPassword || initialPassword.length < 8) {
      toast.error('Tijdelijk wachtwoord moet minimaal 8 tekens zijn');
      return;
    }
    setApproving(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/admin/partner-applications/${detail.id}/approve`,
        { initial_password: initialPassword, notes: approveNotes },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setApproveResult(res.data);
      toast.success('Partner goedgekeurd — welkomstmail verstuurd');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Goedkeuren mislukt');
    }
    setApproving(false);
  };

  const doReject = async (app) => {
    if (!window.confirm(`Aanmelding van ${app.full_name} (${app.email}) afwijzen?`)) return;
    try {
      await axios.post(
        `${BACKEND}/api/admin/partner-applications/${app.id}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Aanmelding afgewezen');
      setDetail(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Afwijzen mislukt');
    }
  };

  return (
    <div style={{ padding: 24, color: 'white' }} data-testid="partner-apps-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 28, fontWeight: 800, margin: 0 }}>Partner-aanmeldingen</h1>
          <p style={{ color: '#94a3b8', marginTop: 4 }}>Beoordeel inkomende partner / callcenter / consultant verzoeken</p>
        </div>
        <button onClick={load} data-testid="partner-apps-refresh" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Verversen
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="In behandeling" value={stats.pending} accent="#f59e0b" />
        <StatCard label="Goedgekeurd" value={stats.approved} accent="#10b981" />
        <StatCard label="Afgewezen" value={stats.rejected} accent="#ef4444" />
        <StatCard label="Totaal" value={stats.pending + stats.approved + stats.rejected} accent="#D4A017" />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="partner-apps-status-filter"
          style={{ background: '#0f172a', color: 'white', border: '1px solid #1e293b', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}
        >
          <option value="">Alle statussen</option>
          <option value="pending">In behandeling</option>
          <option value="approved">Goedgekeurd</option>
          <option value="rejected">Afgewezen</option>
        </select>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          data-testid="partner-apps-kind-filter"
          style={{ background: '#0f172a', color: 'white', border: '1px solid #1e293b', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}
        >
          <option value="">Alle types</option>
          {Object.entries(KIND_BADGE_STYLES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <input
          placeholder="Zoek op naam, e-mail of bedrijf"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="partner-apps-search"
          style={{ flex: 1, minWidth: 200, background: '#0f172a', color: 'white', border: '1px solid #1e293b', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}
        />
      </div>

      {/* Table */}
      <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            <Loader2 size={20} className="animate-spin" /> Laden…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Geen aanmeldingen gevonden</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                <Th>Naam</Th>
                <Th>E-mail</Th>
                <Th>Type</Th>
                <Th>Bedrijf</Th>
                <Th>Status</Th>
                <Th>Datum</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const kindStyle = KIND_BADGE_STYLES[r.kind] || { bg: '#64748b', label: r.kind };
                const statusStyle = STATUS_BADGE_STYLES[r.status] || { bg: '#64748b', label: r.status };
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid #1e293b' }} data-testid={`partner-row-${r.id}`}>
                    <Td>{r.full_name}</Td>
                    <Td style={{ color: '#94a3b8' }}>{r.email}</Td>
                    <Td><Badge color={kindStyle.bg}>{kindStyle.label}</Badge></Td>
                    <Td style={{ color: '#94a3b8' }}>{r.company_name || '—'}</Td>
                    <Td><Badge color={statusStyle.bg}>{statusStyle.label}</Badge></Td>
                    <Td style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>{(r.created_at || '').slice(0, 10)}</Td>
                    <Td>
                      <button onClick={() => setDetail(r)} data-testid={`partner-view-${r.id}`} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Eye size={12} /> Bekijk
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail / Approve modal */}
      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 100 }} onClick={() => !showApprove && setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#1e293b', borderRadius: 14, padding: 24, maxWidth: 640, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 700, margin: 0 }}>{detail.full_name}</h2>
                <p style={{ color: '#94a3b8', fontSize: 13, margin: '4px 0 0' }}>{KIND_BADGE_STYLES[detail.kind]?.label || detail.kind}</p>
              </div>
              <button onClick={() => { setDetail(null); setShowApprove(false); }} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            {/* Approve Result */}
            {approveResult && (
              <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid #10b981', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <p style={{ color: '#10b981', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckCircle size={16} /> Partner geactiveerd
                </p>
                <p style={{ color: '#cbd5e1', fontSize: 13, marginTop: 6 }}>
                  <strong>E-mail:</strong> {approveResult.email}<br/>
                  <strong>Rol:</strong> {approveResult.role}<br/>
                  <strong>Wachtwoord:</strong> <code style={{ background: '#0f172a', padding: '2px 6px', borderRadius: 4 }}>{initialPassword}</code>
                </p>
                {approveResult.promo_code && (
                  <p style={{ color: '#cbd5e1', fontSize: 13, marginTop: 6 }}>
                    <strong>Promo-code:</strong> <code style={{ background: '#0f172a', padding: '2px 6px', borderRadius: 4 }}>{approveResult.promo_code}</code>
                  </p>
                )}
                <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 8 }}>De welkomstmail met intake-link is automatisch verstuurd.</p>
              </div>
            )}

            {/* Approve form */}
            {showApprove && !approveResult && detail.status === 'pending' && (
              <div style={{ background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.3)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <p style={{ color: '#D4A017', fontWeight: 700, margin: 0, marginBottom: 8 }}>Partner goedkeuren</p>
                <p style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 10 }}>
                  Tijdelijk wachtwoord (min 8 tekens) — wordt in welkomstmail meegestuurd. Klant moet dit bij eerste login wijzigen.
                </p>
                <input
                  value={initialPassword}
                  onChange={(e) => setInitialPassword(e.target.value)}
                  data-testid="partner-initial-password"
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #1e293b', color: 'white', padding: '10px 12px', borderRadius: 8, fontFamily: 'monospace', fontSize: 14, marginBottom: 10 }}
                />
                <textarea
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  placeholder="Interne notes (optioneel)"
                  data-testid="partner-approve-notes"
                  rows={2}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #1e293b', color: 'white', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 10 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={doApprove} disabled={approving} data-testid="partner-approve-confirm" style={{ flex: 1, background: '#10b981', color: 'white', fontWeight: 700, padding: '10px 16px', borderRadius: 8, border: 'none', cursor: approving ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    {approving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Bevestig & verstuur welkomstmail
                  </button>
                  <button onClick={() => setShowApprove(false)} disabled={approving} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '10px 16px', borderRadius: 8, cursor: 'pointer' }}>
                    Annuleer
                  </button>
                </div>
              </div>
            )}

            {/* Application details */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
              <DetailField icon={Mail} label="E-mail" value={detail.email} />
              <DetailField icon={Phone} label="Telefoon" value={detail.phone || '—'} />
              <DetailField icon={Building} label="Bedrijf" value={detail.company_name || '—'} />
              <DetailField icon={Globe} label="Website" value={detail.website || '—'} />
              <DetailField icon={MapPin} label="Regio" value={detail.region || '—'} />
              <DetailField icon={Users} label="Bereik" value={detail.audience_size || '—'} />
            </div>
            {detail.experience && (
              <div style={{ background: '#0f172a', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', margin: '0 0 4px', fontWeight: 700 }}>Ervaring</p>
                <p style={{ fontSize: 13, color: '#cbd5e1', margin: 0, whiteSpace: 'pre-wrap' }}>{detail.experience}</p>
              </div>
            )}
            {detail.motivation && (
              <div style={{ background: '#0f172a', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', margin: '0 0 4px', fontWeight: 700 }}>Motivatie</p>
                <p style={{ fontSize: 13, color: '#cbd5e1', margin: 0, whiteSpace: 'pre-wrap' }}>{detail.motivation}</p>
              </div>
            )}

            {/* Actions */}
            {detail.status === 'pending' && !approveResult && !showApprove && (
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => openApprove(detail)} data-testid="partner-approve-open" style={{ flex: 1, background: '#10b981', color: 'white', fontWeight: 700, padding: '12px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <CheckCircle size={16} /> Goedkeuren
                </button>
                <button onClick={() => doReject(detail)} data-testid="partner-reject" style={{ flex: 1, background: '#ef4444', color: 'white', fontWeight: 700, padding: '12px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <XCircle size={16} /> Afwijzen
                </button>
              </div>
            )}
            {detail.status === 'approved' && (
              <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid #10b981', borderRadius: 10, padding: 12, color: '#a7f3d0', fontSize: 13 }}>
                <CheckCircle size={14} style={{ display: 'inline', marginRight: 6 }} />
                Goedgekeurd op {(detail.approved_at || '').slice(0, 10)} — Portal-user: {detail.user_id || '—'}
                {detail.promo_code && (
                  <p style={{ margin: '8px 0 0' }}><Sparkles size={12} style={{ display: 'inline', marginRight: 4 }} /> Promo-code: <code style={{ background: '#0f172a', padding: '2px 6px', borderRadius: 4 }}>{detail.promo_code}</code></p>
                )}
              </div>
            )}
            {detail.status === 'rejected' && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 10, padding: 12, color: '#fca5a5', fontSize: 13 }}>
                <XCircle size={14} style={{ display: 'inline', marginRight: 6 }} />
                Afgewezen op {(detail.rejected_at || '').slice(0, 10)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let r = '';
  for (let i = 0; i < 12; i++) r += chars[Math.floor(Math.random() * chars.length)];
  return r + '!';
}

function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: 14 }}>
      <p style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0, fontWeight: 700 }}>{label}</p>
      <p style={{ color: accent, fontSize: 26, fontWeight: 800, margin: '4px 0 0' }}>{value}</p>
    </div>
  );
}

function Th({ children }) {
  return <th style={{ textAlign: 'left', padding: '12px 14px', color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>;
}

function Td({ children, style }) {
  return <td style={{ padding: '10px 14px', ...style }}>{children}</td>;
}

function Badge({ color, children }) {
  return <span style={{ background: color, color: 'white', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{children}</span>;
}

function DetailField({ icon: Icon, label, value }) {
  return (
    <div style={{ background: '#0f172a', borderRadius: 8, padding: 10 }}>
      <p style={{ fontSize: 10, color: '#94a3b8', margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Icon size={10} /> {label}
      </p>
      <p style={{ fontSize: 13, color: 'white', margin: '3px 0 0', wordBreak: 'break-word' }}>{value}</p>
    </div>
  );
}
